// ─── showcaseService.js ───────────────────────────────────────────────────────
// CRUD for venue_showcases table
// Writes route through create-showcase / update-showcase edge functions
// (admin-locked, service role). Reads use the supabase client; admin RLS
// gates direct queries to admins, public reads are filtered to status='published'.
// ─────────────────────────────────────────────────────────────────────────────
// STATUS MAPPING NOTE:
//   UI uses:  'draft' | 'live'
//   DB uses:  'draft' | 'published' | 'archived'
//   'live' ↔ 'published' are the same concept, map at every boundary.
// ─────────────────────────────────────────────────────────────────────────────
import { supabase, isSupabaseAvailable } from '../lib/supabaseClient';

// Map UI status → DB status
function toDbStatus(s) { return s === 'live' ? 'published' : (s || 'draft'); }
// Map DB status → UI status
function toUiStatus(s) { return s === 'published' ? 'live' : (s || 'draft'); }

// ── Edge function callers ─────────────────────────────────────────────────────
// Writes route through create-showcase / update-showcase (admin-locked, service role).
// Other admin-only mutations (delete, duplicate, template ops) use direct supabase
// queries, which are gated by the admin RLS policy on venue_showcases.

async function invokeCreateShowcase(payload) {
  const { data, error } = await supabase.functions.invoke('create-showcase', { body: payload });
  if (error) throw new Error(error.message || 'create-showcase failed');
  if (!data?.success) throw new Error(data?.error || 'create-showcase failed');
  return data.data;
}

async function invokeUpdateShowcase(id, payload) {
  // Edge functions can cold-start in 15-30s. Retry once on timeout before giving up.
  async function attempt(ms) {
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('__timeout__')), ms)
    );
    const { data, error } = await Promise.race([
      supabase.functions.invoke('update-showcase', { body: { id, ...payload } }),
      timeout,
    ]);
    if (error) throw new Error(error.message || 'update-showcase failed');
    if (!data?.success) throw new Error(data?.error || 'update-showcase failed');
    // No silent saves: a success envelope with no record means the update
    // matched nothing (or the read-back was blocked). Treat as failure.
    if (!data.data) throw new Error('Save not verified, the server returned no record');
    return data.data;
  }
  try {
    return await attempt(30000); // 30s first try
  } catch (e) {
    if (e.message !== '__timeout__') throw e;
    // Cold start timed out, retry once with a fresh call
    try {
      return await attempt(30000);
    } catch (e2) {
      throw new Error(e2.message === '__timeout__'
        ? 'Save timed out, the server is slow to respond. Please try again.'
        : e2.message);
    }
  }
}

// ── Transform: DB row → admin card shape ─────────────────────────────────────
// Extract best thumbnail from sections JSON (hero section: images[] or videoUrl)
function heroThumbFromSections(sections) {
  if (!Array.isArray(sections)) return '';
  const hero = sections.find(s => s.type === 'hero');
  if (!hero?.content) return '';
  // 1. First image in hero images array
  const imgs = hero.content.images;
  if (Array.isArray(imgs) && imgs.length) {
    const first = imgs[0];
    const url = typeof first === 'string' ? first : first?.url;
    if (url) return url;
  }
  // 2. Single hero image field
  if (hero.content.image) return hero.content.image;
  // 3. YouTube video thumbnail
  const videoUrl = hero.content.videoUrl || '';
  const yt = videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/maxresdefault.jpg`;
  return '';
}

function dbToCard(row) {
  const sectionsThumb = heroThumbFromSections(row.sections) || heroThumbFromSections(row.published_sections);
  return {
    id:           row.id,
    type:         row.type         || 'venue',
    name:         row.title,                          // UI uses 'name'
    slug:         row.slug,
    location:     row.location     || '',
    excerpt:      row.excerpt      || '',
    heroImage:    row.hero_image_url || sectionsThumb || '',
    logo:         row.logo_url     || '',
    previewUrl:   row.preview_url  || '',
    listingId:    row.listing_id   || '',
    status:       toUiStatus(row.status),             // 'published' → 'live'
    sections:     Array.isArray(row.sections)  ? row.sections  : [],
    stats:        Array.isArray(row.key_stats) ? row.key_stats : [],
    sortOrder:    row.sort_order   ?? 0,
    publishedAt:       row.published_at || null,
    publishVersion:    row.publish_version || 1,
    publishedSections: Array.isArray(row.published_sections) ? row.published_sections : null,
    lastUpdated:  row.updated_at
      ? new Date(row.updated_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      : '',
    createdAt:    row.created_at,
  };
}

// ── Transform: admin form → DB insert shape ───────────────────────────────────
function formToDb(form, type = 'venue') {
  const now = new Date().toISOString();
  const dbStatus = toDbStatus(form.status);
  return {
    type:            type,
    title:           form.name || form.title || '',
    slug:            form.slug,
    location:        form.location     || null,
    excerpt:         form.excerpt      || null,
    hero_image_url:  form.heroImage    || null,
    logo_url:        form.logo         || null,
    preview_url:     form.previewUrl   || null,
    listing_id:      form.listingId    || null,
    status:          dbStatus,                        // 'live' → 'published'
    sections:        form.sections     || [],
    key_stats:       (form.stats || []).filter(s => s.value && s.label),
    sort_order:      form.sortOrder    ?? 0,
    published_at:    dbStatus === 'published' ? now : null,
    seo_title:       form.seo_title       || null,
    seo_description: form.seo_description || null,
    og_image:        form.og_image        || null,
  };
}

// ── Fetch all showcases (optionally filter by type) ───────────────────────────
export async function fetchShowcases(type = null) {
  if (!isSupabaseAvailable()) return [];
  try {
    // sections included for hero video thumbnail extraction; published_sections for live cards
    const CARD_COLS = 'id,type,title,slug,location,excerpt,hero_image_url,logo_url,preview_url,listing_id,status,key_stats,sort_order,published_at,updated_at,created_at,sections,published_sections';
    let q = supabase
      .from('venue_showcases')
      .select(CARD_COLS)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });

    if (type) q = q.eq('type', type);

    const { data, error } = await q;
    if (error) throw error;
    return (data || []).map(dbToCard);
  } catch (err) {
    console.error('[showcaseService] fetchShowcases error:', err);
    return [];
  }
}

// ── Fetch single showcase by ID (for admin/internal use, returns card shape) ──
export async function fetchShowcaseById(id) {
  if (!isSupabaseAvailable() || !id) return null;
  try {
    const { data, error } = await supabase
      .from('venue_showcases')
      .select('*')
      .eq('id', id)
      .abortSignal(AbortSignal.timeout(10_000))  // fail fast, never hang a save read-back
      .maybeSingle();
    if (error) throw error;
    return data ? dbToCard(data) : null;
  } catch (err) {
    console.error('[showcaseService] fetchShowcaseById error:', err);
    return null;
  }
}

// ── Fetch single showcase by slug (for admin/internal use, returns card shape) ──
export async function fetchShowcaseBySlugCard(slug) {
  if (!isSupabaseAvailable()) return null;
  try {
    const { data, error } = await supabase
      .from('venue_showcases')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw error;
    return data ? dbToCard(data) : null;
  } catch (err) {
    console.error('[showcaseService] fetchShowcaseBySlugCard error:', err);
    return null;
  }
}

// ── Fetch a single showcase by slug (for public rendering) ────────────────────
export async function fetchShowcaseBySlug(slug) {
  if (!isSupabaseAvailable() || !slug) return null;
  try {
    const { data, error } = await supabase
      .from('venue_showcases')
      .select('id, slug, title, type, status, hero_image_url, excerpt, key_stats, published_sections, sections, listing_id, template_key, theme, seo_title, seo_description, og_image, published_at')
      .eq('slug', slug)
      .in('status', ['published', 'draft'])
      .maybeSingle();
    if (error) { console.warn('[showcaseService] fetchShowcaseBySlug:', error.message); return null; }
    return data;
  } catch (e) {
    console.warn('[showcaseService] fetchShowcaseBySlug:', e.message);
    return null;
  }
}

// ── Save draft ────────────────────────────────────────────────────────────────
// Updates only the supplied fields. Routes through update-showcase edge fn.
export async function saveShowcaseDraft(id, updates) {
  if (!isSupabaseAvailable()) throw new Error('Supabase not available');
  return await invokeUpdateShowcase(id, { ...updates, status: 'draft' });
}

// ── Publish showcase ──────────────────────────────────────────────────────────
// Sets status to live and snapshots the supplied sections to published_sections.
// Accepts optional expectedVersion for optimistic locking; returns the raw DB row.
export async function publishShowcase(id, sections, expectedVersion) {
  if (!isSupabaseAvailable()) throw new Error('Supabase not available');
  return await invokeUpdateShowcase(id, {
    status: 'live',
    sections,
    ...(expectedVersion !== undefined && { expectedPublishVersion: expectedVersion }),
  });
}

// ── Duplicate a showcase ───────────────────────────────────────────────────────
// Admin-only direct query: select source row, insert a copy with a new slug.
export async function duplicateShowcase(id) {
  if (!isSupabaseAvailable()) throw new Error('Supabase not available');
  const { data: src, error: getErr } = await supabase
    .from('venue_showcases')
    .select('*')
    .eq('id', id)
    .single();
  if (getErr) throw getErr;
  const newSlug = `${src.slug}-copy-${Date.now()}`;
  const copy = { ...src, id: undefined, slug: newSlug, status: 'draft', published_at: null,
    created_at: undefined, updated_at: undefined };
  const { data: inserted, error: insErr } = await supabase
    .from('venue_showcases')
    .insert(copy)
    .select('*')
    .single();
  if (insErr) throw insErr;
  return dbToCard(inserted);
}

// ── Create new showcase ───────────────────────────────────────────────────────
export async function createShowcase(form, type = 'venue') {
  if (!isSupabaseAvailable()) {
    return { ...dbToCard({ ...formToDb(form, type), id: `temp-${Date.now()}`, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }), _offline: true };
  }
  try {
    // create-showcase edge fn expects flat camelCased fields (title, slug, etc.)
    const row = await invokeCreateShowcase({
      type,
      title:           form.name || form.title || '',
      slug:            form.slug,
      location:        form.location     || null,
      excerpt:         form.excerpt      || null,
      heroImage:       form.heroImage    || null,
      logo:            form.logo         || null,
      previewUrl:      form.previewUrl   || null,
      listingId:       form.listingId    || null,
      status:          toDbStatus(form.status),
      sections:        form.sections     || [],
      stats:           (form.stats || []).filter(s => s.value && s.label),
      sortOrder:       form.sortOrder    ?? 0,
      seoTitle:        form.seo_title       || null,
      seoDescription:  form.seo_description || null,
      ogImage:         form.og_image        || null,
    });
    return dbToCard(row);
  } catch (err) {
    console.error('[showcaseService] createShowcase error:', err.message);
    throw err;
  }
}

// ── Update showcase ───────────────────────────────────────────────────────────
export async function updateShowcase(id, form) {
  if (!isSupabaseAvailable()) return null;
  try {
    const row = await invokeUpdateShowcase(id, {
      title:           form.name || form.title,
      slug:            form.slug,
      location:        form.location,
      excerpt:         form.excerpt,
      heroImage:       form.heroImage,
      logo:            form.logo,
      previewUrl:      form.previewUrl,
      listingId:       form.listingId,
      status:          form.status !== undefined ? toDbStatus(form.status) : undefined,
      sections:        form.sections,
      stats:           form.stats,
      sortOrder:       form.sortOrder,
      seoTitle:        form.seo_title,
      seoDescription:  form.seo_description,
      ogImage:         form.og_image,
    });
    return dbToCard(row);
  } catch (err) {
    console.error('[showcaseService] updateShowcase error:', err.message);
    throw err;
  }
}

// ── Fetch templates (is_template = true) ─────────────────────────────────────
// Direct admin query, admin RLS handles auth; non-admin reads return empty.
export async function fetchTemplates(type = null) {
  if (!isSupabaseAvailable()) return [];
  try {
    let q = supabase
      .from('venue_showcases')
      .select('id, type, title, slug, hero_image_url, template_key, sections')
      .eq('is_template', true)
      .order('sort_order', { ascending: true });
    if (type) q = q.eq('type', type);
    const { data, error } = await q;
    if (error) return [];
    return (data || []).map(row => ({
      id:          row.id,
      key:         row.template_key || row.slug,
      label:       row.title,
      type:        row.type,
      heroImage:   row.hero_image_url || '',
      icon:        row.template_key || '◈',
      sections:    Array.isArray(row.sections) ? row.sections : [],
    }));
  } catch {
    return [];
  }
}

// ── Save current showcase as a reusable template ──────────────────────────────
export async function saveShowcaseAsTemplate(showcase, sections, { label, icon }) {
  if (!isSupabaseAvailable()) throw new Error('Supabase not available');
  const slug = `tmpl-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')}-${Date.now()}`;
  // create-showcase doesn't currently set is_template; fall back to direct
  // admin insert (admin RLS gate) so the template flag persists.
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('venue_showcases')
    .insert({
      type:               showcase?.type || 'venue',
      title:              label,
      slug,
      status:             'draft',
      is_template:        true,
      template_key:       icon || '◈',
      sections,
      published_sections: [],
      hero_image_url:     showcase?.hero_image_url || '',
      sort_order:         0,
      created_at:         now,
      updated_at:         now,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

// ── Clone a template into a new draft showcase ────────────────────────────────
export async function cloneTemplate(templateId, { title, slug }) {
  if (!isSupabaseAvailable()) throw new Error('Supabase not available');
  const { data: tmpl, error: getErr } = await supabase
    .from('venue_showcases')
    .select('*')
    .eq('id', templateId)
    .single();
  if (getErr) throw getErr;
  const copy = {
    ...tmpl,
    id:                 undefined,
    title,
    slug,
    is_template:        false,
    status:             'draft',
    published_at:       null,
    created_at:         undefined,
    updated_at:         undefined,
  };
  const { data: inserted, error: insErr } = await supabase
    .from('venue_showcases')
    .insert(copy)
    .select('*')
    .single();
  if (insErr) throw insErr;
  return dbToCard(inserted);
}

// ── Delete showcase ───────────────────────────────────────────────────────────
export async function deleteShowcase(id) {
  if (!isSupabaseAvailable()) return;
  try {
    const { error } = await supabase.from('venue_showcases').delete().eq('id', id);
    if (error) throw error;
  } catch (err) {
    console.error('[showcaseService] deleteShowcase error:', err);
    throw err;
  }
}
